import React from "react";
import { Button, message, Modal, Tag, TagProps } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { InstitutionCell } from "./InstitutionCell";
import type { Mandate } from "@/providers/smarta-banking/banking-api-client";
import { ApiPaths } from "@/providers/smarta-banking/banking-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { api } from "../../client";

const getTexts = (t: TFunction) => ({
  labels: {
    createdAt: t("mandateTable.labels.createdAt"),
    updatedAt: t("mandateTable.labels.updatedAt"),
  },
  states: {
    enqueued: t("mandateTable.states.enqueued"),
    pending: t("mandateTable.states.pending"),
    processing: t("mandateTable.states.processing"),
    accepted: t("mandateTable.states.accepted"),
    activated: t("mandateTable.states.activated"),
    timeout: t("mandateTable.states.timeout"),
    errored: t("mandateTable.states.errored"),
    renewal: t("mandateTable.states.renewal"),
  },
  deleteModal: {
    title: t("mandateTable.deleteModal.title"),
    content: t("mandateTable.deleteModal.content"),
    okText: t("mandateTable.deleteModal.okText"),
    cancelText: t("mandateTable.deleteModal.cancelText"),
  },
  deleteButton: {
    delete: t("mandateTable.deleteButton.delete"),
    deleting: t("mandateTable.deleteButton.deleting"),
  },
  messages: {
    deleteSuccess: t("mandateTable.messages.deleteSuccess"),
    deleteError: t("mandateTable.messages.deleteError"),
  },
});

export type EnqueuedMandate = Omit<Mandate, "state"> & {
  state: "enqueued";
};
export type MandateTableData = (Mandate | EnqueuedMandate) & {
  type: "mandate";
};

type Props = {
  data: MandateTableData;
  customerId: string;
};

const DELETABLE_STATES: Array<(Mandate | EnqueuedMandate)["state"]> = [
  "timeout",
  "errored",
];

const stateToTagColor: Record<
  (Mandate | EnqueuedMandate)["state"],
  TagProps["color"]
> = {
  enqueued: "cyan",
  pending: "gold",
  processing: "orange",
  accepted: "lime",
  activated: "green",
  timeout: "magenta",
  errored: "red",
  renewal: "blue",
};

export const MandateTable: React.FC<Props> = ({ data, customerId }) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const queryClient = useQueryClient();
  const { institutionId, state, createdAt, updatedAt } = data;

  const canDelete = DELETABLE_STATES.includes(state);

  const { mutate: deleteMandate, isPending: isDeleting } = api.useMutation(
    "delete",
    ApiPaths.deleteApiMandatesByCustomerIdByInstitutionId,
    {
      onSuccess: () => {
        message.success(texts.messages.deleteSuccess);
        queryClient.invalidateQueries({
          queryKey: ["get", ApiPaths.getApiMandatesByCustomerId],
        });
      },
      onError: (error) => {
        message.error(error.error?.message ?? texts.messages.deleteError);
      },
    }
  );

  const handleDeleteClick = () => {
    Modal.confirm({
      title: texts.deleteModal.title,
      icon: <ExclamationCircleOutlined />,
      content: texts.deleteModal.content,
      okText: texts.deleteModal.okText,
      cancelText: texts.deleteModal.cancelText,
      onOk: () => {
        deleteMandate({
          params: {
            path: {
              customerId,
              institutionId,
            },
          },
        });
      },
    });
  };

  return (
    <Tbody>
      <Tr isHeaderRow>
        <InstitutionCell institutionId={institutionId} />

        <Td>
          <Label>{texts.labels.createdAt}</Label>
          <Value>{moment(createdAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td>
          <Label>{texts.labels.updatedAt}</Label>
          <Value>{moment(updatedAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td />
        <Td />
        <Td className="text-center">
          <Tag color={stateToTagColor[state]}>{texts.states[state]}</Tag>
        </Td>
        <Td className="text-right">
          {canDelete && (
            <Button
              type="link"
              size="small"
              onClick={handleDeleteClick}
              loading={isDeleting}
              disabled={isDeleting}
            >
              {isDeleting
                ? texts.deleteButton.deleting
                : texts.deleteButton.delete}
            </Button>
          )}
        </Td>
      </Tr>
    </Tbody>
  );
};
